// Facility HR page — full port of legacy
// `src/app/[locale]/[country]/account/facility/[facilityId]/hr/facility-hr-page-client.tsx`
// (gate wrapper) + `src/components/account/facility-hr-client.tsx` (3 tabs).
//
// Differences vs legacy wrapper:
//   - Feature gate `facility.hr.shift_scheduling` resolved via the SPA's
//     client-side FeatureAccessProvider, mounted locally around the gated
//     content (same approach as provider/team.tsx) — apps/api has no
//     /api/features/access endpoint yet. Backend RBAC still enforces every
//     HR request; this is UX gating only.
//   - Legacy read the caller's facility role from `session.user.facilityTeams`
//     (a claim the apps/api JWT does not carry) and redirected to
//     `/account?reason=no-facility-access` when missing. Here the role comes
//     from the `/hr/staff` response (rows include the caller); a 403/404
//     renders FacilityStateCard "no-access", any other failure "load-failed"
//     with Retry.
//   - Tab state (`?tab=`) is URL-driven via validateSearch per the
//     tabs-and-subtabs pattern; the client receives activeTab/onTabChange.

import { useCallback, useMemo } from "react";

import { Trans } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useSession } from "@pontimed/auth-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CalendarClock } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import {
  FacilityHrClient,
  HR_TAB_IDS,
  type FacilityHrTab,
  type HrStaffMember,
} from "@/components/account/facility/facility-hr-client";
import { FacilityFeatureTeaser } from "@/components/account/facility/facility-feature-teaser";
import {
  FacilityStateCard,
  facilityErrorKind,
} from "@/components/account/facility/facility-state-card";
import {
  FeatureAccessProvider,
  useFeatureAccess,
  useFeatureAccessLoading,
} from "@/components/features/feature-access-provider";
import { Skeleton } from "@/components/ui/skeleton";
import type { OwnerType } from "@/lib/features/types";

interface HrSearch {
  tab: FacilityHrTab;
}

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/hr")({
  validateSearch: (search: Record<string, unknown>): HrSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    return {
      tab: (HR_TAB_IDS as readonly string[]).includes(raw) ? (raw as FacilityHrTab) : "attendance",
    };
  },
  component: FacilityHrPage,
});

// Stable reference — feeds FeatureAccessProvider's memoized scope key.
const HR_PAGE_SCOPES: OwnerType[] = ["user", "facility"];

function FacilityHrPage() {
  const { facilityId } = Route.useParams();

  // Facility scope resolves against THIS facility (route param), not the
  // most-recent facility subscription.
  const ownerIds = useMemo(() => ({ facility: facilityId }), [facilityId]);

  return (
    <FeatureAccessProvider activeScopes={HR_PAGE_SCOPES} ownerIds={ownerIds}>
      <GatedFacilityHr facilityId={facilityId} />
      <AccountSecurityBanner />
    </FeatureAccessProvider>
  );
}

function GatedFacilityHr({ facilityId }: { facilityId: string }) {
  const { user } = useSession();
  const { tab: activeTab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const setActiveTab = useCallback(
    (tab: FacilityHrTab) => void navigate({ to: ".", search: { tab }, replace: false }),
    [navigate],
  );

  // Feature gate — HR Lite is Enterprise-only (soon on pro/starter). Mirrors
  // the legacy page-client's useFeatureAccess("facility.hr.shift_scheduling").
  const access = useFeatureAccess("facility.hr.shift_scheduling");
  const accessLoading = useFeatureAccessLoading();
  const allowed =
    access !== null &&
    (access.access === "included" || (access.access === "limited" && !access.exhausted));

  // Same staff list the legacy wrapper fetched (react-query, gated on access).
  // Rows include the caller — used to resolve currentUserRole (legacy read it
  // from session.user.facilityTeams).
  const staffQ = useQuery({
    queryKey: ["facility", facilityId, "hr", "staff"],
    queryFn: () =>
      apiFetch.get<{ staff: HrStaffMember[] }>(`/api/account/facility/${facilityId}/hr/staff`),
    enabled: allowed,
    retry: false,
  });

  if (accessLoading || access === null || (allowed && staffQ.isLoading)) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!allowed) {
    return <HrFeatureTeaser facilityId={facilityId} />;
  }

  if (staffQ.error) {
    return (
      <FacilityStateCard
        kind={facilityErrorKind(staffQ.error)}
        facilityId={facilityId}
        onRetry={() => void staffQ.refetch()}
        retrying={staffQ.isFetching}
      />
    );
  }

  const staffMembers = staffQ.data?.staff ?? [];
  const currentUserId = user?.userId ?? "";
  const currentUserRole = staffMembers.find((s) => s.userId === currentUserId)?.role ?? "";

  return (
    <FacilityHrClient
      facilityId={facilityId}
      staffMembers={staffMembers}
      currentUserRole={currentUserRole}
      currentUserId={currentUserId}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    />
  );
}

// ─── Feature teaser (plan without HR Lite) ──────────────────────────────────
//
// Shifts, attendance (worked hours computed) and leave approvals exist; the
// payroll export does not yet. Shift swaps aren't built, so the shift bullet
// no longer promises them.

function HrFeatureTeaser({ facilityId }: { facilityId: string }) {
  return (
    <FacilityFeatureTeaser
      facilityId={facilityId}
      pageTitle={<Trans>HR / Staff</Trans>}
      icon={CalendarClock}
      title={<Trans>HR Lite for your clinic</Trans>}
      bullets={[
        { label: <Trans>Shift scheduling for doctors and staff</Trans> },
        { label: <Trans>Attendance tracking with automatic time calculation</Trans> },
        { label: <Trans>Leave and time-off requests with approval flow</Trans> },
        { label: <Trans>Payroll export (CSV/PDF)</Trans>, soon: true },
      ]}
    />
  );
}
